import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

/**
 * Enhanced WebSocket hook with:
 * - Automatic reconnection
 * - Fallback to polling when disconnected
 * - Room-based connections
 * - Authentication support
 */
export function useSocket(apiUrl, eventHandlers = {}, options = {}) {
  const {
    role = 'admin', //  'admin' | 'kitchen' | 'customer'
    tableCode = null,
    branchId = null, // ไม่ใส่ = โหมด HQ rollup (เห็นเฉพาะ event ที่ broadcast เข้า admin:all)
    token = null,
    fallbackEnabled = false, // Changed from true - reduces API overhead
    fallbackInterval = 30000, // 30 seconds
    onConnect = null,
    onDisconnect = null,
    onError = null
  } = options;

  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const fallbackTimerRef = useRef(null);
  const fallbackTimeoutRef = useRef(null); // Track setTimeout to prevent overlap
  const disconnectTimeRef = useRef(null);

  // eventHandlers มักเป็น closure ที่อ้างอิง state ของ component ที่เรียกใช้ (เช่น
  // Dashboard.jsx's loadRevenueStats() ที่อ่าน dateRange ปัจจุบัน) แต่ effect ข้างล่าง
  // ตั้งใจไม่ใส่ eventHandlers ใน dependency array (กันต่อ socket ใหม่ทุกครั้งที่ re-render
  // เพราะ object literal {...} ที่ส่งเข้ามาเป็น reference ใหม่ทุก render) — ถ้า socket.on()
  // ผูกกับ handler ตรงๆ แบบเดิม จะได้ handler จาก render แรกสุดค้างตลอดอายุ connection
  // (stale closure) เช่น event ยิงมาทีหลังตอนผู้ใช้เปลี่ยนแท็บวันที่ไปแล้ว แต่ handler ที่ค้าง
  // อยู่ยังคำนวณช่วงวันที่แบบตอน mount ทำให้การ์ดรายได้โชว์เลขผิดช่วงเวลาเงียบๆ — เก็บ
  // handlers ล่าสุดไว้ใน ref แล้วอ่านผ่าน ref ตอน event ยิงจริงแทน อัปเดตทุก render โดยไม่ต้อง
  // พึ่ง dependency array ของ effect ที่ตั้ง connection
  const eventHandlersRef = useRef(eventHandlers);
  useEffect(() => {
    eventHandlersRef.current = eventHandlers;
  });

  // Fallback polling function
  const fallbackPoll = useCallback(() => {
    if (fallbackEnabled && onDisconnect) {
      console.log('[WebSocket] Using fallback polling...');
      onDisconnect(); // Trigger data reload
    }
  }, [fallbackEnabled, onDisconnect]);

  useEffect(() => {
    // Don't connect if no API URL
    if (!apiUrl) return;

    // Parse backend URL for WebSocket connection
    let socketUrl = apiUrl;

    if (apiUrl.startsWith('http://localhost') || apiUrl.includes('localhost')) {
      socketUrl = apiUrl.replace('/api', '');
    } else if (apiUrl.startsWith('https://backend')) {
      socketUrl = apiUrl;
    } else if (!apiUrl.startsWith('http')) {
      socketUrl = window.location.origin.replace('admin.', 'backend.');
    }

    console.log(`[WebSocket] Connecting to: ${socketUrl} as ${role}`);

    // Create socket connection with query params for room assignment
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000, // Increased from 1000ms to 2000ms to reduce reconnect frequency
      reconnectionDelayMax: 10000, // Increased from 5000ms to 10000ms for max backoff
      reconnectionAttempts: Infinity,
      timeout: 10000,
      query: {
        role,
        tableCode,
        branchId,
        token
      }
    });

    socketRef.current = socket;

    // Connection event handlers
    socket.on('connect', () => {
      console.log(`[WebSocket] Connected: ${socket.id} as ${role}`);
      setConnected(true);
      setReconnecting(false);

      // Clear fallback timeout (prevents starting polling)
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }

      // Clear fallback timer (stops active polling)
      if (fallbackTimerRef.current) {
        clearInterval(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }

      disconnectTimeRef.current = null;

      if (onConnect) {
        onConnect(socket);
      }
    });

    socket.on('disconnect', (reason) => {
      console.log(`[WebSocket] Disconnected: ${reason}`);
      setConnected(false);
      disconnectTimeRef.current = Date.now();

      if (onDisconnect) {
        onDisconnect(reason);
      }

      // Start fallback polling after 10 seconds of disconnect
      // Clear any existing timeout first to prevent overlap
      if (fallbackEnabled && !fallbackTimerRef.current && !fallbackTimeoutRef.current) {
        fallbackTimeoutRef.current = setTimeout(() => {
          if (!socket.connected) {
            console.log('[WebSocket] Starting fallback polling...');
            fallbackTimerRef.current = setInterval(fallbackPoll, fallbackInterval);
          }
          fallbackTimeoutRef.current = null; // Clear reference after execution
        }, 10000); // Wait 10s before starting fallback
      }
    });

    socket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error.message);
      setReconnecting(true);

      if (onError) {
        onError(error);
      }
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log(`[WebSocket] Reconnected after ${attemptNumber} attempts`);
      setReconnecting(false);

      const disconnectDuration = disconnectTimeRef.current
        ? Date.now() - disconnectTimeRef.current
        : 0;

      console.log(`[WebSocket] Was disconnected for ${Math.round(disconnectDuration / 1000)}s`);
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`[WebSocket] Reconnection attempt #${attemptNumber}`);
      setReconnecting(true);
    });

    socket.on('reconnect_failed', () => {
      console.error('[WebSocket] Reconnection failed - all attempts exhausted');
      setReconnecting(false);
    });

    // Register custom event handlers — เรียกผ่าน eventHandlersRef.current เสมอ (ดูคอมเมนต์
    // ที่ประกาศ ref ด้านบน) ไม่ใช่ตัวแปร handler ที่ capture ไว้ตอน effect นี้รันครั้งแรก
    Object.keys(eventHandlers).forEach((event) => {
      socket.on(event, (data) => {
        console.log(`[WebSocket] Received event: ${event}`, data);
        eventHandlersRef.current[event]?.(data);
      });
    });

    // Cleanup on unmount
    return () => {
      console.log('[WebSocket] Disconnecting...');

      // Clear fallback timeout
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }

      // Clear fallback timer
      if (fallbackTimerRef.current) {
        clearInterval(fallbackTimerRef.current);
      }

      // Remove event listeners
      Object.entries(eventHandlers).forEach(([event]) => {
        socket.off(event);
      });

      socket.disconnect();
    };
  }, [apiUrl, role, tableCode, branchId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    socket: socketRef.current,
    connected,
    reconnecting
  };
}
